import React from "react";
import { OpenInNew } from "@mui/icons-material";
import { Box, Divider, Paper, Stack, Typography } from "@mui/material";
import { RegistryVariable } from "../../registry/sitePageRegistry";
import { SEOForm } from "../../types";
import { resolveTemplateForPreview } from "../../utils/templateTokens";

interface SearchPreviewCardProps {
  form: SEOForm;
  previewUrl: string;
  variables?: RegistryVariable[];
}

const SearchPreviewCard: React.FC<SearchPreviewCardProps> = ({
  form,
  previewUrl,
  variables = [],
}) => {
  const resolve = (raw: string) => resolveTemplateForPreview(raw, variables);
  const title = resolve(form.metaTitle) || "Untitled page";
  const description = resolve(form.metaDescription) || "No description set.";
  const ogTitle = resolve(form.ogTitle) || title;
  const ogDescription = resolve(form.ogDescription) || description;
  const twitterTitle = resolve(form.twitterTitle) || ogTitle;
  const twitterDescription = resolve(form.twitterDescription) || ogDescription;
  const twitterImage = form.twitterImageUrl || form.ogImageUrl;

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <OpenInNew fontSize="small" color="action" />
        <Typography fontWeight={800}>Search Previews</Typography>
      </Box>

      <Stack spacing={2.5}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Google Search
          </Typography>
          <Typography
            sx={{ color: "#1a0dab", fontSize: 18, lineHeight: 1.3, mt: 0.5 }}
          >
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: "#006621" }}>
            {previewUrl}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {description.length > 160
              ? `${description.slice(0, 157)}...`
              : description}
          </Typography>
        </Box>

        <Divider />

        <Box>
          <Typography variant="caption" color="text.secondary">
            Facebook / Open Graph
          </Typography>
          {form.ogImageUrl && (
            <Box
              component="img"
              src={form.ogImageUrl}
              alt="OG preview"
              sx={{
                width: "100%",
                maxHeight: 160,
                objectFit: "cover",
                borderRadius: 1,
                mt: 0.5,
              }}
              onError={(event) => {
                (event.target as HTMLImageElement).style.display = "none";
              }}
            />
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}
          >
            {previewUrl}
          </Typography>
          <Typography fontWeight={700} variant="body2">
            {ogTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {ogDescription}
          </Typography>
        </Box>

        <Divider />

        <Box>
          <Typography variant="caption" color="text.secondary">
            Twitter / X
          </Typography>
          <Box
            sx={{ display: "flex", gap: 1.5, alignItems: "center", mt: 0.5 }}
          >
            {twitterImage && (
              <Box
                component="img"
                src={twitterImage}
                alt="Twitter preview"
                sx={{
                  width: 64,
                  height: 64,
                  objectFit: "cover",
                  borderRadius: 1,
                  flexShrink: 0,
                }}
                onError={(event) => {
                  (event.target as HTMLImageElement).style.display = "none";
                }}
              />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={700} variant="body2" noWrap>
                {twitterTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {twitterDescription}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {previewUrl}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Stack>
    </Paper>
  );
};

export default SearchPreviewCard;
